/**
 * 音声アップロードコンポーネント
 * ドラッグ&ドロップまたはファイル選択で音声ファイルをアップロード
 */
import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Alert,
  IconButton,
  Paper,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { bg, border, statusBg, text as textColors } from '../../../../utils/darkModeAdapter';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ALLOWED_FORMATS = [
  'audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/m4a', 'audio/x-m4a', 'audio/webm', 'audio/ogg',
  'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/avi'
];
const ALLOWED_EXTENSIONS = ['.wav', '.mp3', '.m4a', '.webm', '.ogg', '.mp4', '.mov', '.avi'];
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB

const AudioUploader = ({
  onFileSelect,
  onUpload,
  uploading = false,
  uploadProgress = 0,
  error = null,
  disabled = false,
}) => {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  // ファイルのバリデーション
  const validateFile = useCallback((file) => {
    if (!file) return 'ファイルが選択されていません';

    // ファイル形式チェック（MIMEタイプまたは拡張子）
    const fileName = file.name.toLowerCase();
    const isValidFormat = ALLOWED_FORMATS.includes(file.type) ||
      ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));

    if (!isValidFormat) {
      return 'サポートされていないファイル形式です。WAV, MP3, M4A, WebM, OGG, MP4, MOV, AVI形式に対応しています。';
    }

    // ファイルサイズチェック
    if (file.size > MAX_FILE_SIZE) {
      return `ファイルサイズが大きすぎます。最大${MAX_FILE_SIZE / 1024 / 1024}MBまでアップロード可能です。`;
    }

    return null;
  }, []);

  // ファイル選択ハンドラ
  const handleFileSelect = useCallback((file) => {
    const validationResult = validateFile(file);

    if (validationResult) {
      setValidationError(validationResult);
      setSelectedFile(null);
      return;
    }

    setValidationError(null);
    setSelectedFile(file);

    if (onFileSelect) {
      onFileSelect(file);
    }
  }, [validateFile, onFileSelect]);

  // ドラッグ&ドロップハンドラ
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [disabled, handleFileSelect]);

  // ファイル入力ハンドラ
  const handleInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // ファイル選択ダイアログを開く
  const openFileDialog = useCallback(() => {
    if (fileInputRef.current && !disabled) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  // ファイル削除
  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setValidationError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // アップロード実行
  const handleUpload = useCallback(() => {
    if (selectedFile && onUpload) {
      onUpload(selectedFile);
    }
  }, [selectedFile, onUpload]);

  // ファイルサイズのフォーマット
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* エラー表示 */}
      {(error || validationError) && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error || validationError}
        </Alert>
      )}

      {/* ドロップゾーン */}
      {!selectedFile && (
        <Paper
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFileDialog}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? '#249BE0' : border(theme).light,
            borderRadius: 3,
            p: 4,
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: dragOver ? statusBg(theme).info : bg(theme).subtle,
            transition: 'all 0.2s ease',
            opacity: disabled ? 0.6 : 1,
            '&:hover': disabled ? {} : {
              borderColor: '#249BE0',
              backgroundColor: statusBg(theme).info,
            },
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".wav,.mp3,.m4a,.webm,.ogg,.mp4,.mov,.avi,audio/*,video/mp4,video/quicktime"
            onChange={handleInputChange}
            style={{ display: 'none' }}
            disabled={disabled}
          />

          <CloudUploadIcon
            sx={{
              fontSize: 48,
              color: dragOver ? '#249BE0' : '#94a3b8',
              mb: 2,
            }}
          />

          <Typography variant="h6" sx={{ color: textColors(theme).primary, mb: 1 }}>
            音声ファイルをドラッグ&ドロップ
          </Typography>

          <Typography variant="body2" sx={{ color: textColors(theme).secondary, mb: 2 }}>
            または
          </Typography>

          <Button
            variant="outlined"
            disabled={disabled}
            sx={{
              borderColor: '#249BE0',
              color: '#249BE0',
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': {
                borderColor: '#1976d2',
                backgroundColor: statusBg(theme).info,
              },
            }}
          >
            ファイルを選択
          </Button>

          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: textColors(theme).muted }}>
            対応形式: WAV, MP3, M4A, WebM, OGG (最大500MB)
          </Typography>
        </Paper>
      )}

      {/* 選択されたファイル */}
      {selectedFile && (
        <Paper
          sx={{
            border: `1px solid ${border(theme).light}`,
            borderRadius: 3,
            p: 3,
            backgroundColor: bg(theme).subtle,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AudioFileIcon sx={{ fontSize: 40, color: '#249BE0' }} />

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                {selectedFile.name}
              </Typography>
              <Typography variant="body2" sx={{ color: textColors(theme).secondary }}>
                {formatFileSize(selectedFile.size)}
              </Typography>
            </Box>

            {!uploading && (
              <IconButton
                onClick={handleRemoveFile}
                sx={{ color: textColors(theme).muted, '&:hover': { color: '#ef4444' } }}
              >
                <DeleteIcon />
              </IconButton>
            )}

            {uploadProgress === 100 && (
              <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 28 }} />
            )}
          </Box>

          {/* プログレスバー */}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: border(theme).light,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: '#249BE0',
                  },
                }}
              />
              <Typography variant="caption" sx={{ color: textColors(theme).secondary, mt: 1, display: 'block' }}>
                アップロード中... {uploadProgress}%
              </Typography>
            </Box>
          )}

          {/* アップロードボタン */}
          {!uploading && uploadProgress !== 100 && (
            <Button
              variant="contained"
              onClick={handleUpload}
              fullWidth
              sx={{
                mt: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#249BE0',
                '&:hover': {
                  backgroundColor: '#1976d2',
                },
              }}
            >
              アップロード
            </Button>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default AudioUploader;
